import { faroCoreApiClient } from "@api/faro-core-api-client";
import { useErrorHandler } from "@components/error-boundary/error-handling-context";
import { getMemberDisplayName } from "@utils/data-display";
import { useGroupsOfWorkspace } from "@hooks/use-groups-of-workspace";
import { useMembersOfGroup } from "@utils/hooks/use-members-of-group";
import { useEffect, useMemo, useState } from "react";
import {
  CloneProjectPayload,
  ProjectCloneHelperReturnProps,
} from "@pages/project-profile/project-clone/project-clone-types";
import { isCompanyContext } from "@utils/type-guards";
import { useMembersOfWorkspace } from "@hooks/use-members-of-workspace";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { ProjectEvents } from "@utils/track-event/track-event-list";
import { memberStateSelector } from "@store/member/member-slice";
import { fetchMember } from "@store/member/member-thunks";
import { fetchCompany } from "@store/workspace/workspace-thunks";
import { useAppDispatch, useAppSelector } from "@store/store-helper";
import { companyStateSelector } from "@store/workspace/workspace-slice";
import { BaseProjectProps } from "@custom-types/base-types";
import { useToast } from "@flat-ui";
import { FaroCoreApiUtils } from "@stellar/core-api-client";

const DEBOUNCE_IN_MMS = 500;

/** Helper functions for cloning project */
export function useProjectCloneHelper({
  project,
}: BaseProjectProps): ProjectCloneHelperReturnProps {
  const sourceCompanyId = isCompanyContext(project.context)
    ? project.context.company.id
    : undefined;
  const { handleErrorWithDialog, handleErrorWithToast } = useErrorHandler();
  const { trackEvent } = useTrackEvent();
  const { openToast } = useToast();

  const dispatch = useAppDispatch();

  const [shouldAddToCurrentCompany, setShouldAddToCurrentCompany] =
    useState<boolean>(true);
  const [shouldAddToAuthorizedMember, setShouldAddToAuthorizedMember] =
    useState<boolean>(true);
  const [shouldAddProjectMembers, setShouldAddProjectMembers] =
    useState<boolean>(false);

  const initialCloneProjectPayload: CloneProjectPayload = {
    sourceProjectId: project.id,
    projectName: `CLONE - ${project.name.toString()}`,
    targetCompanyId: sourceCompanyId ?? "",
    targetGroupId: "",
    authorizedBy: "",
    projectManager: "",
    options: {
      // eslint-disable-next-line @typescript-eslint/naming-convention -- key comes from API
      cloneDirectProjectMembers: false,
    },
  };

  const [cloneProjectPayload, setCloneProjectPayload] =
    useState<CloneProjectPayload>(initialCloneProjectPayload);

  const [debouncedCompanyId, setDebouncedCompanyId] = useState(
    cloneProjectPayload.targetCompanyId
  );
  const [isCloningProject, setIsCloningProject] = useState<boolean>(false);
  const [shouldShowDialog, setShouldShowDialog] = useState<boolean>(false);
  const [debouncedProjectManager, setDebouncedProjectManager] = useState(
    cloneProjectPayload.projectManager
  );

  // Debounce effect for company ID
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedCompanyId(cloneProjectPayload.targetCompanyId);
    }, DEBOUNCE_IN_MMS);

    return () => {
      clearTimeout(handler);
    };
  }, [cloneProjectPayload.targetCompanyId]);

  /** Set companyId based on the currentCompany switch status */
  useEffect(() => {
    if (shouldAddToCurrentCompany && sourceCompanyId) {
      setCloneProjectPayload((prev) => ({
        ...prev,
        targetCompanyId: sourceCompanyId,
      }));
    }

    // Reset the company ID if not adding to current company
    if (!shouldAddToCurrentCompany) {
      setCloneProjectPayload((prev) => ({
        ...prev,
        targetCompanyId: "",
      }));
    }
  }, [shouldAddToCurrentCompany, sourceCompanyId]);

  /** Set project manager ID based on the shouldAddToAuthorizedMember switch status */
  useEffect(() => {
    if (shouldAddToAuthorizedMember) {
      setCloneProjectPayload((prev) => ({
        ...prev,
        projectManager: cloneProjectPayload.authorizedBy,
      }));
    }

    // Reset the project manager if project manager and authorized member should not be the same
    if (!shouldAddToAuthorizedMember) {
      setCloneProjectPayload((prev) => ({
        ...prev,
        projectManager: "",
      }));
    }
  }, [shouldAddToAuthorizedMember, cloneProjectPayload.authorizedBy]);

  function getCompanyName(): string {
    if (debouncedCompanyId) {
      if (company?.name) {
        return company.name;
      } else {
        return "No workspace found with this ID";
      }
    } else {
      return " ";
    }
  }

  function shouldDisableConfirmButton(): boolean {
    return (
      Object.values(cloneProjectPayload).some((value) => !value) ||
      company === undefined ||
      member === undefined
    );
  }

  // Debounce effect for project manager ID
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedProjectManager(cloneProjectPayload.projectManager);
    }, DEBOUNCE_IN_MMS);

    return () => {
      clearTimeout(handler);
    };
  }, [cloneProjectPayload.projectManager]);

  const { fetchingCompany, company } = useAppSelector(companyStateSelector);

  // Fetches the company
  useEffect(() => {
    async function fetchCompanyData(): Promise<void> {
      try {
        await dispatch(
          fetchCompany({ companyId: debouncedCompanyId })
        ).unwrap();
      } catch (error) {
        handleErrorWithToast({
          id: `useProjectCloneHelper-${Date.now().toString()}`,
          title: "Could not load workspace",
          error,
        });
      }
    }

    if (debouncedCompanyId) {
      void fetchCompanyData();
    }
  }, [dispatch, debouncedCompanyId, handleErrorWithToast]);

  const { groups, isFetchingWorkspaceGroups } = useGroupsOfWorkspace({
    companyId: debouncedCompanyId,
    errorHandler: handleErrorWithToast,
  });

  const { groupMembers, isFetchingGroupMembers } = useMembersOfGroup({
    companyId: debouncedCompanyId,
    groupId: cloneProjectPayload.targetGroupId,
    errorHandler: handleErrorWithToast,
  });

  const { workspaceMembers, isFetchingWorkspaceMembers } =
    useMembersOfWorkspace({
      companyId: debouncedCompanyId,
      errorHandler: handleErrorWithToast,
    });

  const { fetchingMember, member } = useAppSelector(memberStateSelector);

  // Fetches the member
  useEffect(() => {
    async function fetchMemberData(): Promise<void> {
      try {
        await dispatch(
          fetchMember({ memberId: debouncedProjectManager })
        ).unwrap();
      } catch (error) {
        handleErrorWithToast({
          id: `useProjectCloneHelper-${Date.now().toString()}`,
          title: "Could not load member",
          error,
        });
      }
    }
    if (debouncedProjectManager) {
      void fetchMemberData();
    }
  }, [debouncedProjectManager, dispatch, handleErrorWithToast]);

  /** List of all the authorized members. It can be EA of company and GM of selected group and it must be active */
  const authorizedMembers = useMemo(() => {
    const companyExecutives = workspaceMembers
      .filter(
        (companyMember) =>
          companyMember.role === "COMPANY_EXECUTIVE" &&
          FaroCoreApiUtils.Member.isMemberActive(companyMember)
      )
      .map((companyMember) => companyMember);

    const groupManagers = groupMembers
      .filter(
        (groupMember) =>
          groupMember.role === "COMPANY_MANAGER" &&
          FaroCoreApiUtils.Member.isMemberActive(groupMember.user)
      )
      .map(({ user }) => user);

    // Removes the duplicates from the combined list of company executives and group managers (A member might be in both lists)
    const authorizedMembersWithoutDuplication = Array.from(
      new Map(
        [...companyExecutives, ...groupManagers].map((item) => [
          item.identity,
          item,
        ])
      ).values()
    );

    // Sort members by the display name (case-insensitive)
    return authorizedMembersWithoutDuplication.sort((memberA, memberB) => {
      return memberA.email.localeCompare(memberB.email, undefined, {
        sensitivity: "base",
      });
    });
  }, [workspaceMembers, groupMembers]);

  function getMemberName(): string {
    if (member) {
      return getMemberDisplayName(member);
    } else if (debouncedProjectManager) {
      return "No member found with this ID";
    }
    return " ";
  }

  function onCloneProjectPayloadChange<T extends keyof CloneProjectPayload>(
    key: T,
    value: CloneProjectPayload[T]
  ): void {
    setCloneProjectPayload((prev) => ({
      ...prev,
      [key]: value,
    }));
  }

  function handleDialogClose(): void {
    setShouldShowDialog(false);
    setCloneProjectPayload(initialCloneProjectPayload);
  }

  async function onCloneProject(): Promise<void> {
    trackEvent({
      name: ProjectEvents.cloneProject,
      props: {
        isSameCompany: cloneProjectPayload.targetCompanyId === sourceCompanyId,
        targetCompanyId: cloneProjectPayload.targetCompanyId,
        targetGroupId: cloneProjectPayload.targetGroupId,
        authorizedUserId: cloneProjectPayload.authorizedBy,
        projectManagerId: cloneProjectPayload.projectManager,
        isSameAuthorizedUser:
          cloneProjectPayload.authorizedBy ===
          cloneProjectPayload.projectManager,
        isCloningProjectMembers: shouldAddProjectMembers,
      },
    });

    try {
      setIsCloningProject(true);
      await faroCoreApiClient.admin.cloneProject({
        targetCompanyId: cloneProjectPayload.targetCompanyId,
        targetGroupId: cloneProjectPayload.targetGroupId,
        cloneProject: {
          sourceProjectId: cloneProjectPayload.sourceProjectId,
          projectManager: cloneProjectPayload.projectManager,
          projectName: cloneProjectPayload.projectName,
          authorizedBy: cloneProjectPayload.authorizedBy,
          options: {
            // eslint-disable-next-line @typescript-eslint/naming-convention -- key comes from API
            cloneDirectProjectMembers: shouldAddProjectMembers,
          },
        },
      });

      openToast({
        title: "Project is cloned successfully",
      });
      handleDialogClose();
    } catch (error) {
      handleErrorWithDialog({
        id: `use-project-clone-helper-${Date.now().toString()}`,
        title: "The project cannot be cloned",
        error,
      });
    } finally {
      setIsCloningProject(false);
    }
  }

  return {
    cloneProjectPayload,
    fetching: {
      isFetchingCompany: fetchingCompany.selectedCompany === "pending",
      isFetchingCompanyMembers: isFetchingWorkspaceMembers,
      isFetchingGroups: isFetchingWorkspaceGroups,
      isFetchingGroupMembers,
      isFetchingMember: fetchingMember.selectedMember === "pending",
      isCloningProject,
    },
    groups,
    authorizedMembers,
    getCompanyName,
    getMemberName,
    onCloneProjectPayloadChange,
    onCloneProject,
    handleDialogClose,
    shouldShowDialog,
    setShouldShowDialog,
    shouldDisableConfirmButton,
    shouldAddToCurrentCompany,
    setShouldAddToCurrentCompany,
    shouldAddToAuthorizedMember,
    setShouldAddToAuthorizedMember,
    shouldAddProjectMembers,
    setShouldAddProjectMembers,
  };
}
